import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useCart } from "@/contexts/ShopContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import FooterSection from "@/components/FooterSection";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const { items, clear } = useCart();
  const { currency, formatAmount } = useCurrency();
  const { user, isAuthenticated, openAuth } = useAuth();
  const navigate = useNavigate();

  const [productMap, setProductMap] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "India",
    phone: "",
    email: "",
  });

  // Prefill from user
  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        email: user.email || f.email,
        phone: user.phone || f.phone,
        full_name: user.name || f.full_name,
      }));
    }
  }, [user]);

  // Load product details to compute totals
  useEffect(() => {
    if (items.length === 0) return;
    const ids = items.map((it) => it.product_id);
    Promise.all(ids.map((id) => api.get(`/products/${id}`).then((r) => r.data).catch(() => null)))
      .then((arr) => {
        const m = {};
        arr.forEach((p) => { if (p) m[p.id] = p; });
        setProductMap(m);
      });
  }, [items]);

  if (items.length === 0) {
    return (
      <div className="bg-midnight min-h-screen">
        <Navbar />
        <div className="pt-40 text-center px-6">
          <div className="eyebrow mb-3">Empty bag</div>
          <h2 className="font-serif text-white text-4xl">Your selection is empty.</h2>
          <button
            onClick={() => navigate("/")}
            className="mt-8 inline-flex border border-white/25 px-7 py-3 text-[11px] tracking-[0.32em] uppercase text-white hover:border-white"
          >
            Browse the house
          </button>
        </div>
      </div>
    );
  }

  const subtotal = items.reduce((s, it) => {
    const p = productMap[it.product_id];
    if (!p) return s;
    const price = currency === "INR" ? p.price_inr : p.price_usd;
    return s + price * it.quantity;
  }, 0);
  const shipping = currency === "INR" ? (subtotal >= 50000 ? 0 : 999) : (subtotal >= 500 ? 0 : 25);
  const total = subtotal + shipping;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.message("Please sign in to complete your order.");
      openAuth();
      return;
    }
    // Validate required fields
    const required = ["full_name", "line1", "city", "state", "postal_code", "phone"];
    for (const k of required) {
      if (!form[k]?.trim()) {
        toast.error("Please complete the shipping address.");
        return;
      }
    }
    setSubmitting(true);
    const ok = await loadRazorpay();
    if (!ok) { toast.error("Could not load payment provider."); setSubmitting(false); return; }

    let orderResp;
    try {
      orderResp = await api.post("/orders/create", {
        items: items.map((it) => ({
          product_id: it.product_id,
          size: it.size || null,
          color: it.color || null,
          quantity: it.quantity,
        })),
        currency,
        shipping_address: form,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create order.");
      setSubmitting(false);
      return;
    }

    const data = orderResp.data;
    const rzp = new window.Razorpay({
      key: data.razorpay_key_id || process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: data.amount,
      currency: data.currency,
      name: "Asymmetry Edits",
      description: `Order ${data.order_id}`,
      order_id: data.razorpay_order_id,
      prefill: {
        name: form.full_name,
        email: form.email || user?.email || "",
        contact: form.phone,
      },
      theme: { color: "#0a0b10" },
      handler: async (res) => {
        try {
          await api.post("/orders/verify", {
            order_id: data.order_id,
            razorpay_order_id: res.razorpay_order_id,
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_signature: res.razorpay_signature,
          });
          clear();
          toast.success("Payment confirmed. Welcome to the house.");
          navigate(`/order-success?order=${data.order_id}`);
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Payment verification failed.");
        } finally {
          setSubmitting(false);
        }
      },
      modal: {
        ondismiss: () => setSubmitting(false),
      },
    });
    rzp.open();
  };

  const field = (name, label, type = "text", required = true) => (
    <div>
      <label className="text-[10px] tracking-[0.35em] uppercase text-silver/70">{label}{required ? " *" : ""}</label>
      <input
        data-testid={`checkout-${name}`}
        type={type}
        required={required}
        value={form[name]}
        onChange={(e) => setForm({ ...form, [name]: e.target.value })}
        className="lux-input"
      />
    </div>
  );

  return (
    <div className="bg-midnight min-h-screen">
      <Navbar />
      <main className="pt-28 md:pt-36 pb-20 px-6 md:px-12 lg:px-24" data-testid="checkout-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <div className="eyebrow mb-3">Checkout</div>
          <h1 className="font-serif text-white text-4xl md:text-6xl leading-[0.95]">
            One last <span className="italic text-silver">step</span>.
          </h1>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <form onSubmit={handleSubmit} className="lg:col-span-7 space-y-10" data-testid="checkout-form">
            {!isAuthenticated && (
              <div className="border border-silver/40 bg-white/5 p-5">
                <div className="eyebrow mb-2 text-silver">Sign in required</div>
                <p className="text-white/70 text-sm">
                  Sign in or create an account to complete checkout and access your order history.
                </p>
                <button
                  type="button"
                  onClick={openAuth}
                  data-testid="checkout-signin-cta"
                  className="mt-4 inline-flex border border-white/30 px-6 py-3 text-[11px] tracking-[0.32em] uppercase text-white hover:border-white"
                >
                  Sign in
                </button>
              </div>
            )}

            <section>
              <h2 className="font-serif text-white text-2xl mb-6 border-b border-white/8 pb-3">Shipping</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {field("full_name", "Full name")}
                {field("phone", "Phone", "tel")}
                <div className="md:col-span-2">{field("line1", "Address line 1")}</div>
                <div className="md:col-span-2">{field("line2", "Address line 2", "text", false)}</div>
                {field("city", "City")}
                {field("state", "State")}
                {field("postal_code", "Postal code")}
                {field("country", "Country")}
                <div className="md:col-span-2">{field("email", "Email (for receipt)", "email", false)}</div>
              </div>
            </section>

            <button
              type="submit"
              disabled={submitting}
              data-testid="checkout-pay-btn"
              className="w-full md:w-auto bg-white text-black px-10 py-4 uppercase tracking-[0.32em] text-[11px] hover:bg-silver disabled:opacity-50 transition-colors"
            >
              {submitting ? "Processing…" : `Pay ${formatAmount(total)}`}
            </button>
          </form>

          {/* Summary */}
          <aside className="lg:col-span-5 lg:pl-8">
            <div className="border border-white/8 p-6 md:p-8 bg-midnight-deep">
              <div className="eyebrow mb-4">Order Summary</div>
              <ul className="divide-y divide-white/8 mb-6">
                {items.map((it) => {
                  const p = productMap[it.product_id];
                  if (!p) return null;
                  const price = currency === "INR" ? p.price_inr : p.price_usd;
                  return (
                    <li key={`${it.product_id}-${it.size}-${it.color}`} className="flex gap-4 py-4" data-testid={`summary-${it.product_id}`}>
                      <div className="w-16 h-20 bg-midnight-panel shrink-0 overflow-hidden">
                        <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-serif text-white text-base">{p.name}</div>
                        <div className="text-[10px] tracking-[0.28em] uppercase text-white/55 mt-1">
                          {it.size && <span className="mr-2">{it.size}</span>}
                          {it.color && <span className="mr-2">{it.color}</span>}
                          <span>×{it.quantity}</span>
                        </div>
                      </div>
                      <div className="text-silver text-sm tracking-[0.2em]">{formatAmount(price * it.quantity)}</div>
                    </li>
                  );
                })}
              </ul>
              <div className="space-y-3 border-t border-white/8 pt-4 text-white">
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">Subtotal</span>
                  <span>{formatAmount(subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">Shipping</span>
                  <span>{shipping === 0 ? "Complimentary" : formatAmount(shipping)}</span>
                </div>
                <div className="flex justify-between text-lg font-serif border-t border-white/8 pt-3">
                  <span className="text-silver/80 text-[10px] tracking-[0.35em] uppercase pt-2">Total</span>
                  <span data-testid="checkout-total">{formatAmount(total)}</span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </main>
      <FooterSection />
    </div>
  );
}
